import React from 'react';

import { StudioVideo, StudioVideoStatus } from '@/features/ai-video-creator/stores/studioVideoStore';

import ErrorDisplay from './ErrorDisplay';
import ProgressBar from './ProgressBar';
import VideoPlayer, { type VideoPlayerOptions } from './VideoPlayer';
import { BlockStack, Box, Icon, Text } from '@shopify/polaris';
import { AlertTriangleIcon } from '@shopify/polaris-icons';

export type VideoCardContentOptions = VideoPlayerOptions;

interface VideoCardContentProps {
  video: StudioVideo;
  options?: VideoPlayerOptions;
  status: StudioVideoStatus;
  progress: number;
  hasError: boolean;
  showProgressBannerWarning?: boolean;
}

const VideoCardContent: React.FC<VideoCardContentProps> = ({
  video,
  status,
  progress,
  hasError,
  options,
  showProgressBannerWarning = false,
}) => {
  if (status === StudioVideoStatus.DRAFT) {
    return <ErrorDisplay message="Draft" isDraft />;
  }

  if (hasError) {
    return <ErrorDisplay message="Generation failed" />;
  }

  if (status === StudioVideoStatus.PROCESSING) {
    return (
      <>
        {showProgressBannerWarning ? (
          <div className="flex h-full w-full items-center justify-between">
            <BlockStack gap="800">
              <ProgressBar progress={progress} />
              <Box paddingInlineStart="400" paddingInlineEnd="400">
                <Text as="p" variant="bodyXs" tone="caution">
                  <Icon source={AlertTriangleIcon} /> Due to increased demand, generations may take a little longer.
                </Text>
              </Box>
            </BlockStack>
          </div>
        ) : (
          <ProgressBar progress={progress} />
        )}
      </>
    );
  }

  return <VideoPlayer video={video} options={options} />;
};

export default VideoCardContent;
